import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store';
import { ShieldCheck } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiErrorMessage } from '../lib/api';

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const { verifyEmail } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const attemptedRef = useRef(false);

  // Resend mini-form state (rendered only in the error state).
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendFailed, setResendFailed] = useState(false);

  useEffect(() => {
    // Guard against React StrictMode double-invoking this effect (dev only),
    // so the verification request is only ever sent once per mount.
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    const run = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(t('auth.verifyEmailMissingToken'));
        return;
      }
      try {
        await verifyEmail(token);
        setStatus('success');
      } catch (error) {
        console.error('Verify email error:', error);
        setStatus('error');
        setErrorMessage(getApiErrorMessage(error, t('auth.verifyEmailError')));
      }
    };

    run();
  }, [token, verifyEmail, t]);

  // Once verified, brief success message then redirect to the dashboard
  // (the session is already opened inside verifyEmail).
  useEffect(() => {
    if (status !== 'success') return;
    const timer = setTimeout(() => navigate('/dashboard'), 2000);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResending(true);
    setResendMessage('');
    setResendFailed(false);

    try {
      await authService.resendVerification(resendEmail);
      setResendMessage(t('auth.resendVerificationSuccessMessage'));
    } catch (error) {
      console.error('Resend verification error:', error);
      setResendMessage(getApiErrorMessage(error, t('auth.resendVerificationError')));
      setResendFailed(true);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthShell title={t('auth.verifyEmailTitle')}>
<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('auth.verifyingEmail')}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-xl p-4">
                <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                <p className="text-sm text-green-700 dark:text-green-300">
                  {t('auth.verifyEmailSuccessMessage')}
                </p>
              </div>
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                {t('auth.verifyingRedirecting')}
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
              </div>

              {/* Resend verification mini-form */}
              <form onSubmit={handleResend} className="space-y-4">
                <div>
                  <label htmlFor="resend-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('auth.email')}
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="resend-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="input-field"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                {resendMessage && (
                  <p className={`text-sm ${resendFailed ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {resendMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isResending}
                  className="btn-brand text-sm"
                >
                  {isResending ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink"></div>
                  ) : (
                    t('auth.resendVerificationButton')
                  )}
                </button>
              </form>

              <div className="text-center">
                <Link
                  to="/login"
                  className="font-medium text-brand-700 hover:text-brand-600"
                >
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </div>
          )}
        </div>
    </AuthShell>
  );
};

export default VerifyEmail;